/* import __COLOCATED_TEMPLATE__ from './fly-input.hbs.js'; */
var _dec, _dec2, _class, _descriptor, _descriptor2, _descriptor3;
function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : String(i); }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'transform-class-properties is enabled and runs after the decorators transform.'); }
import { action, computed } from '@ember/object';
import { notEmpty } from '@ember/object/computed';
import Component from '@glimmer/component';
import { isEmpty } from '@ember/utils';
import { errorsArrayToString, ErrorType } from '@customerio/fly-components/utils/errors';
import { tracked } from '@glimmer/tracking';
/**
  The `fly-input` component displays an input field for a value, including
  notification and display of any associated errors for that value.

  To use it, bind in a `value`, `onInput` or `onChange`, and the `errors` for
  the property you want to display. For example, if you have a normal Ember Data
  model with server-generated errors:

  ```handlebars
  <FlyInput
    @value={{this.model.title}}
    @onInput={{fn (mut this.model.title)}}
    @errors={{model.errors.title}}
  />
  ```

  In this case, if `this.model.errors.title` is empty, the input field will display
  normally. But if there are errors, the field will have an error state, and
  a tooltip will display the error text.

  @class FlyInput
  @public
*/
let FlyInput = (_dec = computed('args.errors.[]'), _dec2 = notEmpty('args.errors'), (_class = class FlyInput extends Component {
  get inputClassNames() {
    return this.args.inputClassNames ?? 'fly-form-control';
  }
  get clearable() {
    return this.args.clearable ?? false;
  }
  get inline() {
    return this.args.inline ?? false;
  }
  get autofocus() {
    return this.args.autofocus ?? false;
  }
  get errorType() {
    return this.args.errorType ?? ErrorType.Tooltip;
  }
  get disabled() {
    return this.args.disabled ?? false;
  }
  get actualType() {
    if (this.args.type === 'password' && this.showPasswordAsText) {
      return 'text';
    }
    return this.args.type;
  }
  get size() {
    if (this.renderAsSearch) {
      return this.args.size ?? 'sm';
    }
    return;
  }

  // Keep this computed in for backwards compatibility. Older uses of FlyInput
  // may be making nested/partial updates to the errors array
  get errorMessage() {
    let {
      errors
    } = this.args;
    if (isEmpty(errors)) {
      return '';
    }
    return errorsArrayToString(errors);
  }
  get inputClass() {
    let {
      inputClassNames,
      errorType,
      inline,
      hasError,
      renderAsSearch,
      size
    } = this;
    let classes = [inputClassNames];
    if (inline) {
      classes.push('fly-form-control--inline');
    }
    if (hasError && !inline && errorType === ErrorType.Tooltip) {
      classes.push('fly-input__control--embedded');
    }
    if (size) {
      classes.push(`fly-form-control--${size}`);
    }
    if (renderAsSearch) {
      classes.push('pl-4');
    }
    return classes.join(' ');
  }
  get shouldDisplayClearButton() {
    if (this.args.value?.length === 0) {
      return false;
    }
    if (this.renderAsSearch) {
      return this.args.clearable ?? true;
    }
    return this.clearable;
  }
  get errorsTooltipClass() {
    if (this.inline) {
      return '';
    }
    return `fly-input__warning--embedded ${this.shouldDisplayClearButton ? 'fly-input__warning--offset' : ''}`;
  }
  get isTooltipError() {
    return this.errorType === ErrorType.Tooltip;
  }
  constructor(owner, args) {
    super(owner, args);
    _initializerDefineProperty(this, "showPasswordAsText", _descriptor, this);
    _initializerDefineProperty(this, "inputElement", _descriptor2, this);
    _defineProperty(this, "renderAsSearch", false);
    _defineProperty(this, "ErrorType", ErrorType);
    _initializerDefineProperty(this, "hasError", _descriptor3, this);
    if (args.onInput == null && args.onChange == null) {
      throw new TypeError('You must pass either `onInput` or `onChange` to FlyInput. FlyInput no longer updates @value on its own.');
    }
  }
  onInputAction(event) {
    this.args.onInput?.(this.castValue(event.target.value), event);
  }
  onChangeAction(event) {
    this.args.onChange?.(this.castValue(event.target.value), event);
  }
  onFocusAction(event) {
    this.args.onFocus?.(event);
  }
  onBlurAction(event) {
    // This is especially visible during tests, but if an input is being destroyed, then this blur
    // action could run as it's being destroyed (or after). We need to avoid running `set` in those
    // cases to avoid errors in ember
    if (this.isDestroyed || this.isDestroying) {
      return;
    }
    this.args.onBlur?.(this.castValue(event.target.value), event);
  }
  clearInput(event) {
    this.args.onChange?.('', event);
    this.args.onInput?.('', event);
    this.args.onClearInput?.(event);
    this.inputElement?.focus?.();
  }
  handleKeyDown(event) {
    switch (event.key) {
      case 'Enter':
        if (this.args.onEnter) {
          this.args.onEnter(event);
        }
        break;
      case 'Escape':
        if (this.clearable) {
          this.clearInput(event);
        }
        break;
    }
  }
  handleKeyUp(event) {
    if (this.args.onKeyUp) {
      this.args.onKeyUp(event);
    }
  }
  didInsertInput(element) {
    this.inputElement = element;
  }
  castValue(value) {
    if (this.args.type === 'number' && value !== '') {
      return Number(value);
    }
    return value;
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "showPasswordAsText", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "inputElement", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "errorMessage", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "errorMessage"), _class.prototype), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "hasError", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "onInputAction", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onInputAction"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onChangeAction", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onChangeAction"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onFocusAction", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onFocusAction"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onBlurAction", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onBlurAction"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "clearInput", [action], Object.getOwnPropertyDescriptor(_class.prototype, "clearInput"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleKeyDown", [action], Object.getOwnPropertyDescriptor(_class.prototype, "handleKeyDown"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleKeyUp", [action], Object.getOwnPropertyDescriptor(_class.prototype, "handleKeyUp"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "didInsertInput", [action], Object.getOwnPropertyDescriptor(_class.prototype, "didInsertInput"), _class.prototype)), _class));
export { FlyInput as default };